// =============================================================================
// Presenting mode — T-508
//
// One toggle that masks every dollar figure in the app.
//
// The problem it solves is NOT permissions. It's the situation where the
// permissions are all working correctly: James is a director, he is entitled to
// see the money, and he is sharing his screen with Josh, a client, or a subbie.
// Clint and Josh raised exactly this — "it's good to go into the ops space and
// be showing the guys, and to have finance right there with banners and totals
// is just a bit risky, at a glance it has a lot."
//
// Why a toggle and not a passcode on the Finance page:
//   * The risk is an EVENT you can see coming, not a permanent state. A passcode
//     charges you every day for something that happens occasionally.
//   * A Finance-page passcode wouldn't have covered the exposure anyway — the
//     project detail page shows contract value, invoiced, paid, outstanding and
//     forecast margin, and it isn't a finance route.
//
// Lifetime: sessionStorage. It survives a page refresh — you do not want a
// stray F5 mid-meeting to unmask the numbers — and dies when the tab or browser
// closes, so it can't silently persist into next week.
//
// WHAT THIS DELIBERATELY DOES NOT TOUCH:
//   PDFs, Excel exports and anything written to the database. Those go through
//   their own formatters (features/pos/poPdf.ts, features/quotes/quotePdf.ts,
//   projectFinancialsExport.ts, exportToTemplate.ts, reportDatasets.ts) which do
//   not import from lib/dates. Verified before this was written — a document
//   generated while presenting still contains the real numbers.
//   The rule: MASK WHAT IS RENDERED, NEVER MASK WHAT IS STORED OR EXPORTED.
// =============================================================================
import { create } from "zustand";

const KEY = "coretel-presenting";

function readInitial(): boolean {
  try { return window.sessionStorage.getItem(KEY) === "1"; }
  catch { return false; }                       // storage blocked — not fatal
}

interface PresentingState {
  presenting: boolean;
  toggle: () => void;
  setPresenting: (v: boolean) => void;
}

export const usePresenting = create<PresentingState>((set) => ({
  presenting: readInitial(),
  toggle: () => set((s) => {
    const next = !s.presenting;
    try { window.sessionStorage.setItem(KEY, next ? "1" : "0"); } catch { /* ignore */ }
    return { presenting: next };
  }),
  setPresenting: (v) => set(() => {
    try { window.sessionStorage.setItem(KEY, v ? "1" : "0"); } catch { /* ignore */ }
    return { presenting: v };
  }),
}));

// The mask. Same visual width regardless of the number underneath, so a table
// doesn't reflow when you flip it — and so nobody can infer magnitude from the
// column width, which a "$•,•••" style mask would leak.
export const MONEY_MASK = "$ ••••••";

// Non-reactive read, for plain functions like fmtMoney that aren't components.
// Components re-render because App.tsx subscribes to the store, which recreates
// the element tree below it — see the comment there.
export function isPresenting(): boolean {
  return usePresenting.getState().presenting;
}

// Wrap any already-formatted money string. Used by the shared formatters in
// lib/dates.ts and by the handful of screens that roll their own.
export function maskMoney(formatted: string): string {
  return isPresenting() ? MONEY_MASK : formatted;
}
